/**
 * Spec 200 — full-screen photo lightbox (Fase 4).
 *
 * Mobile uses an `expo-image` zoomable wrapper; on the web the
 * native `<img>` + `transform: scale()` covers the same need with
 * zero deps. Three controls:
 *
 *   - keyboard left/right (arrow) cycles through the photo set,
 *   - zoom toggle (+ / -) zooms 1× ↔ 2.4×,
 *   - download saves the image (HEIC/JPEG bytes are already in the
 *     `localPath` URL after decrypt).
 *
 * Built on `@radix-ui/react-dialog` for the modal mechanics — focus
 * trap, focus restore, scroll lock, Escape-to-close, click-outside
 * dismissal, ARIA `role="dialog"` + `aria-modal="true"` are all
 * handled by Radix and verified upstream against WAI-ARIA APG.
 * The custom keymap (←/→/+/-) lives outside the Dialog primitives
 * because they're navigation-inside-modal, not modal lifecycle.
 */
import { useCallback, useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ChevronLeft, ChevronRight, Download, X, ZoomIn, ZoomOut } from 'lucide-react';

import type { Photo } from '../types';

import './PhotoViewer.css';

interface PhotoViewerProps {
  photos: Photo[];
  initialIndex: number;
  onClose: () => void;
}

export function PhotoViewer({ photos, initialIndex, onClose }: PhotoViewerProps) {
  const [index, setIndex] = useState(Math.max(0, Math.min(initialIndex, photos.length - 1)));
  const [zoomed, setZoomed] = useState(false);

  const prev = useCallback(() => {
    setIndex((i) => (i - 1 + photos.length) % photos.length);
    setZoomed(false);
  }, [photos.length]);
  const next = useCallback(() => {
    setIndex((i) => (i + 1) % photos.length);
    setZoomed(false);
  }, [photos.length]);

  // Keyboard navigation that's NOT modal lifecycle (Radix handles
  // Escape + Tab focus trap natively). Arrows cycle, +/- zooms.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft') {
        prev();
      } else if (e.key === 'ArrowRight') {
        next();
      } else if (e.key === '+' || e.key === '=') {
        setZoomed(true);
      } else if (e.key === '-') {
        setZoomed(false);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [prev, next]);

  if (photos.length === 0) return null;
  const photo = photos[index];

  function download() {
    if (!photo.localPath) return;
    const a = document.createElement('a');
    a.href = photo.localPath;
    a.download = `${photo.id}.jpg`;
    a.click();
  }

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="pv-backdrop" data-testid="photo-viewer">
          <Dialog.Content
            className="pv-content"
            aria-labelledby="pv-counter"
            onPointerDownOutside={(e) => {
              // Only the backdrop dismisses; clicks on the inner
              // toolbar / image / arrows must NOT close.
              if ((e.target as HTMLElement).closest('.pv-content')) {
                e.preventDefault();
              }
            }}
          >
            {/* Visually hidden title for assistive tech — the visible
                counter at the top doubles as the labelled-by anchor. */}
            <Dialog.Title className="pv-sr-only">Visor de fotos</Dialog.Title>
            <div className="pv-toolbar">
              <span
                id="pv-counter"
                className="pv-counter"
                data-testid="photo-viewer-counter"
              >
                {index + 1} / {photos.length}
              </span>
              <button
                type="button"
                className="pv-icon-btn"
                onClick={() => setZoomed((z) => !z)}
                data-testid="photo-viewer-zoom"
                aria-label={zoomed ? 'Reducir' : 'Ampliar'}
              >
                {zoomed ? <ZoomOut size={20} /> : <ZoomIn size={20} />}
              </button>
              <button
                type="button"
                className="pv-icon-btn"
                onClick={download}
                data-testid="photo-viewer-download"
                aria-label="Descargar"
                disabled={!photo.localPath}
                title={photo.localPath ? 'Descargar' : 'Descifrando…'}
              >
                <Download size={20} />
              </button>
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="pv-icon-btn pv-close"
                  data-testid="photo-viewer-close"
                  aria-label="Cerrar"
                >
                  <X size={24} />
                </button>
              </Dialog.Close>
            </div>

            {photos.length > 1 && (
              <button
                type="button"
                className="pv-arrow pv-arrow-left"
                onClick={prev}
                data-testid="photo-viewer-prev"
                aria-label="Anterior"
              >
                <ChevronLeft size={32} />
              </button>
            )}

            <div className="pv-stage">
              <img
                src={photo.localPath}
                alt=""
                className={zoomed ? 'pv-image pv-image--zoomed' : 'pv-image'}
                data-testid="photo-viewer-image"
                onClick={() => setZoomed((z) => !z)}
                draggable={false}
              />
            </div>

            {photos.length > 1 && (
              <button
                type="button"
                className="pv-arrow pv-arrow-right"
                onClick={next}
                data-testid="photo-viewer-next"
                aria-label="Siguiente"
              >
                <ChevronRight size={32} />
              </button>
            )}
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
